import { clsx } from 'clsx/lite'
import type { ComponentProps, ReactNode } from 'react'
import { Section } from '../elements/section'
import { CheckmarkIcon } from '../icons/checkmark-icon'

export function Plan({
  name,
  price,
  period,
  note,
  subheadline,
  badge,
  features,
  cta,
  className,
}: {
  name: ReactNode
  price: ReactNode
  period?: ReactNode
  note?: ReactNode
  subheadline: ReactNode
  badge?: ReactNode
  features: ReactNode[]
  cta?: ReactNode
} & ComponentProps<'div'>) {
  return (
    <div
      className={clsx(
        'flex flex-col gap-6 rounded-xl bg-mauve-950/2.5 p-6 sm:items-start dark:bg-white/5',
        cta ? 'justify-between' : '',
        className,
      )}
    >
      <div className="self-stretch">
        <div className="flex items-center justify-between">
          {badge && (
            <div className="order-last inline-flex rounded-full bg-mauve-950/10 px-2 text-xs/6 font-medium text-mauve-950 dark:bg-white/10 dark:text-white">
              {badge}
            </div>
          )}

          <h3 className="text-2xl/8 tracking-tight text-mauve-950 dark:text-white">{name}</h3>
        </div>
        <p className="mt-1 inline-flex flex-wrap items-baseline gap-x-1.5 text-base/7">
          <span className="text-mauve-950 dark:text-white">{price}</span>
          {period && <span className="text-mauve-500 dark:text-mauve-500">{period}</span>}
        </p>
        {note ? <p className="mt-0.5 text-xs/5 text-mauve-500 dark:text-mauve-500">{note}</p> : null}
        <div className="mt-4 flex flex-col gap-4 text-sm/6 text-mauve-700 dark:text-mauve-400">{subheadline}</div>
        <ul className="mt-4 space-y-2 text-sm/6 text-mauve-700 dark:text-mauve-400">
          {features.map((feature, index) => (
            <li key={index} className="flex gap-4">
              <CheckmarkIcon className="h-lh shrink-0 stroke-mauve-950 dark:stroke-white" />
              <p>{feature}</p>
            </li>
          ))}
        </ul>
      </div>
      {cta ? cta : null}
    </div>
  )
}

export function PricingMultiTier({
  plans,
  ...props
}: {
  plans: ReactNode
} & ComponentProps<typeof Section>) {
  return (
    <Section {...props}>
      <div className="grid grid-cols-1 gap-2 sm:has-[>:nth-child(5)]:grid-cols-2 sm:max-lg:has-[>:last-child:nth-child(even)]:grid-cols-2 lg:auto-cols-fr lg:grid-flow-col lg:grid-cols-none lg:has-[>:nth-child(5)]:grid-flow-row lg:has-[>:nth-child(5)]:grid-cols-3">
        {plans}
      </div>
    </Section>
  )
}
